import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CategoryOutlined as CategoryIcon,
  CheckCircleOutline as CheckIcon,
  Close as CloseIcon,
  LabelOutlined as TypeIcon,
  NotesOutlined as DescIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import CategoriaService from '../../services/categorias';

// Warm pastel palette — matches app header (#fcf8f2) and background (#f6ede1)
const ACCENT        = '#c0714a';   // terracotta cálido
const ACCENT_LIGHT  = '#fde8d4';   // melocotón pastel
const ACCENT_BG     = '#fff8f2';   // crema muy cálida (form background)
const PAGE_BG       = '#fdf5ec';   // beige suave (entre #fcf8f2 y #f6ede1)
const HEADER_BG     = '#fcf8f2';   // igual que el header de la app

const TYPE_COLOR = {
  producto:  { bg: '#fdecea', color: '#b94040' },  // rosa coral pastel
  servicio:  { bg: '#fef6e4', color: '#9a6010' },  // ámbar cálido pastel
  digital:   { bg: '#e8f6ee', color: '#2e7d55' },  // menta pastel
};

const typeChipSx = (type) => {
  const t = TYPE_COLOR[type?.toLowerCase()] || { bg: ACCENT_LIGHT, color: ACCENT };
  return { backgroundColor: t.bg, color: t.color, fontWeight: 700, fontSize: '0.7rem', height: 22 };
};

const EMPTY_FORM = { name: '', type: '', description: '' };

/* ─── CategoryCard ─────────────────────────────────────────── */
const CategoryCard = ({ cat }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: '#f0e0d0',
      borderLeft: `4px solid ${ACCENT}`,
      p: { xs: 1.5, sm: 2 },
      backgroundColor: '#fffaf6',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 2px 10px rgba(192,113,74,0.12)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: '50%',
          backgroundColor: ACCENT_LIGHT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CategoryIcon sx={{ fontSize: 18, color: ACCENT }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
            {cat.name}
          </Typography>
          {cat.type && (
            <Chip label={cat.type} size="small" sx={typeChipSx(cat.type)} />
          )}
        </Box>
        {cat.description && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.2 }}>
            {cat.description}
          </Typography>
        )}
      </Box>
    </Box>
  </Paper>
);

/* ─── Skeleton loader ──────────────────────────────────────── */
const CardSkeleton = () => (
  <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #f0e0d0', borderLeft: `4px solid ${ACCENT_LIGHT}`, backgroundColor: '#fffaf6', p: 2 }}>
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Skeleton variant="circular" width={36} height={36} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="55%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton width="80%" height={16} />
      </Box>
    </Box>
  </Paper>
);

/* ─── Main screen ──────────────────────────────────────────── */
const Categorias = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState('');
  const [successId, setSuccessId] = useState(null);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CategoriaService.getAll();
      setCategories(data || []);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'El nombre es requerido';
    if (!form.type.trim()) errs.type = 'El tipo es requerido';
    return errs;
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const created = await CategoriaService.create(form);
      const newCat = created || { ...form, _id: Date.now().toString() };
      setCategories((prev) => [newCat, ...prev]);
      setSuccessId(newCat._id || newCat.id || Date.now());
      setForm(EMPTY_FORM);
      setErrors({});
      setShowForm(false);
      setTimeout(() => setSuccessId(null), 2500);
    } catch {
      setErrors({ submit: 'Error al guardar. Intentá de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const filtered = categories.filter((c) =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: PAGE_BG,
        overflow: 'hidden',
      }}
    >
      {/* ── Sticky header ─────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: HEADER_BG,
          borderBottom: `1px solid ${ACCENT_LIGHT}`,
          backdropFilter: 'blur(4px)',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ color: ACCENT, fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: ACCENT, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
            Categorías
          </Typography>
          {!loading && (
            <Chip
              label={categories.length}
              size="small"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, backgroundColor: ACCENT_LIGHT, color: ACCENT, border: 'none' }}
            />
          )}
        </Box>
        <Button
          startIcon={showForm ? <CloseIcon /> : <AddIcon />}
          variant={showForm ? 'outlined' : 'contained'}
          size="small"
          onClick={() => (showForm ? handleCancel() : setShowForm(true))}
          sx={
            showForm
              ? { borderColor: ACCENT, color: ACCENT, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: ACCENT_LIGHT } }
              : { backgroundColor: ACCENT, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#a8603d' } }
          }
        >
          {showForm ? 'Cancelar' : 'Nueva categoría'}
        </Button>
      </Box>

      {/* ── Scrollable content ────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>

        {/* ── Create form (collapsible) ──────────────── */}
        <Collapse in={showForm} unmountOnExit>
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${ACCENT_LIGHT}`,
              backgroundColor: ACCENT_BG,
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: ACCENT, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.8 }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Nueva categoría
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Name + Type row */}
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Nombre *"
                  placeholder="ej: Ropa, Calzado, Accesorios"
                  value={form.name}
                  onChange={handleChange('name')}
                  size="small"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon sx={{ fontSize: 16, color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Tipo *"
                  placeholder="ej: producto, servicio"
                  value={form.type}
                  onChange={handleChange('type')}
                  size="small"
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TypeIcon sx={{ fontSize: 16, color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Description */}
              <TextField
                label="Descripción"
                placeholder="Descripción opcional de la categoría"
                value={form.description}
                onChange={handleChange('description')}
                size="small"
                fullWidth
                multiline
                minRows={2}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <DescIcon sx={{ fontSize: 16, color: ACCENT }} />
                    </InputAdornment>
                  ),
                }}
              />

              {errors.submit && (
                <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                  {errors.submit}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleCancel}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                  sx={{
                    backgroundColor: ACCENT,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#a8603d' },
                  }}
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Collapse>

        {/* ── Search ────────────────────────────────── */}
        {!loading && categories.length > 0 && (
          <TextField
            placeholder="Buscar por nombre o tipo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}

        {/* ── Category count / section label ─────────── */}
        {!loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {search
                ? `${filtered.length} resultado${filtered.length !== 1 ? 's' : ''}`
                : `${categories.length} categoría${categories.length !== 1 ? 's' : ''}`}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        )}

        {/* ── List ──────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                borderRadius: 2,
                border: `1px dashed ${ACCENT_LIGHT}`,
                backgroundColor: '#fffaf6',
              }}
            >
              <CategoryIcon sx={{ fontSize: 40, color: ACCENT_LIGHT, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {search ? 'No hay resultados para esa búsqueda' : 'No hay categorías. Creá la primera.'}
              </Typography>
              {!search && (
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  variant="outlined"
                  onClick={() => setShowForm(true)}
                  sx={{ mt: 2, borderColor: ACCENT, color: ACCENT, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: ACCENT_LIGHT } }}
                >
                  Nueva categoría
                </Button>
              )}
            </Box>
          ) : (
            filtered.map((cat) => (
              <Box
                key={cat._id || cat.id}
                sx={{
                  transition: 'transform 0.25s',
                  ...(successId === (cat._id || cat.id) && {
                    transform: 'scale(1.01)',
                    outline: `2px solid ${ACCENT_LIGHT}`,
                    borderRadius: 2,
                  }),
                }}
              >
                <CategoryCard cat={cat} />
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Categorias;

